import { useCallback, useMemo, useRef, useState } from 'react';
import { SxStyle } from '../../../../types';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogContent from '@mui/material/DialogContent';
import { useStringsContext } from '../../../../contexts/strings';
import DialogActions from '@mui/material/DialogActions';
import { FormControl, Grid } from '@mui/material';
import RenderModuleField from '../../../common/fields/render-fields';
import BaseDialog, { BaseDialogProps } from '../../../common/base-dialog';
import { MatchConfig } from '../utils/data';
import { getEditMatchConfigSchema } from '../UI-schema';
import { doc, setDoc } from 'firebase/firestore';
import { useSnackbar } from 'notistack';
import firebaseApp from '../../../../firebase-app';
import { isAllFieldsValid } from '../../../../utils/team';
import { getMatchesConfigPath } from '../../../../utils/paths';
import { useDocumentData } from 'react-firebase-hooks/firestore';

interface Props extends Pick<BaseDialogProps, 'open' | 'onClose'> {
  onDone: () => void;
  leagueAbbrv: string;
  season: string;
  matchId: number;
  matchConfig?: MatchConfig;
}

const styles: SxStyle = {
  contentList: {
    padding: '16px',
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100px',
  },
};

function EditMatchConfigModal({ open, onClose, onDone, leagueAbbrv, season, matchId }: Props) {
  const { strings } = useStringsContext();
  const { enqueueSnackbar } = useSnackbar();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const valuesFields = useRef<Record<string, any>>({});
  const editedFields = useRef<Record<string, boolean>>({});
  const matchesConfigPath = getMatchesConfigPath(leagueAbbrv, season);

  const [matchConfig, matchConfigLoading] = useDocumentData(
    doc(firebaseApp.db, `${matchesConfigPath}/${matchId}`)
  );

  const onSubmit = useCallback(async () => {
    setSubmitLoading(true);
    try {
      const updatedMatchConfig: Partial<MatchConfig> = {
        id: matchId,
        skipPush:
          valuesFields.current.skipPush !== undefined
            ? valuesFields.current.skipPush
            : matchConfig?.skipPush,
        hideStats: {
          ballPossesion:
            valuesFields.current.hideBallPossesion !== undefined
              ? valuesFields.current.hideBallPossesion
              : matchConfig?.hideStats?.ballPossesion,
          distance:
            valuesFields.current.hideDistance !== undefined
              ? valuesFields.current.hideDistance
              : matchConfig?.hideStats?.distance,
        },
        showInterruptedMatchInformation:
          valuesFields.current.showInterruptedMatchInformation !== undefined
            ? valuesFields.current.showInterruptedMatchInformation
            : matchConfig?.showInterruptedMatchInformation,
        interruptedMatchText:
          valuesFields.current.interruptedMatchText !== undefined
            ? valuesFields.current.interruptedMatchText
            : matchConfig?.interruptedMatchText,
        hideMatchTime:
          valuesFields.current.hideMatchTime !== undefined
            ? valuesFields.current.hideMatchTime
            : matchConfig?.hideMatchTime,
      };

      await setDoc(doc(firebaseApp.db, `${matchesConfigPath}/${matchId}`), updatedMatchConfig, {
        merge: true,
      });

      onDone();
      onClose?.();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(strings.messages.saveError, {
        variant: 'error',
      });
    }
    setSubmitLoading(false);
  }, [matchConfig, matchId, onDone, onClose, enqueueSnackbar, strings, matchesConfigPath]);

  const editMatchConfigUiScheme = useMemo(() => {
    if (!matchConfigLoading) {
      return getEditMatchConfigSchema(matchConfig as MatchConfig);
    }
    return null;
  }, [matchConfigLoading, matchConfig]);

  const onChange = (fieldKey: string, value: any) => {
    valuesFields.current[fieldKey] = value;
    setIsDirty(true);
  };

  const onValidation = async (
    fieldKey: string,
    value: string | number | boolean,
    validation: any
  ) => {
    const isCurrentFieldValid: boolean = await validation.isValid(value);
    editedFields.current[fieldKey] = isCurrentFieldValid;
    const isFormValid = isAllFieldsValid(editedFields.current);
    setSubmitDisabled(!isFormValid);
  };

  return (
    <BaseDialog open={open} onClose={onClose} title={strings.matches.matchSettings}>
      <DialogContent sx={styles.contentList} dividers>
        <FormControl fullWidth sx={styles.content}>
          <Grid item container spacing={3} direction="column">
            {editMatchConfigUiScheme?.fields.map((field) => (
              <Grid item xs key={field.id}>
                <RenderModuleField
                  field={field}
                  setChange={onChange}
                  setValidation={onValidation}
                  path="matches"
                  fullWidth
                />
              </Grid>
            ))}
          </Grid>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {strings.actions.cancel}
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          disabled={submitDisabled && !isDirty}
          loading={submitLoading}
          onClick={onSubmit}
        >
          {strings.actions.save}
        </LoadingButton>
      </DialogActions>
    </BaseDialog>
  );
}

export default EditMatchConfigModal;
